/**
 * Feeds the signed-in tenancy's customer feature flags onto the app's flag store.
 *
 * ## Adding and reading a flag
 *
 * 1. **Declare it twice, identically** — `commons/features/__init__.py`
 *    (`FEATURE_FLAGS`) and `@authe/fe-commons/features` (`FEATURE_FLAGS`), with
 *    its default, kind and owner. `featuresParity.test.ts` fails if they drift;
 *    a name read anywhere must be declared.
 * 2. **Read it** with `useFeatureFlag("my_flag")` (or `<Flagged name="my_flag">`)
 *    from `@authe/ui-core/flags`. Nothing else: not this component, not the
 *    source, not the response.
 * 3. **Set it** per customer / org / tenant / user in the admin portal (a
 *    client-config `features` row; internal-only to write). The most specific
 *    rung wins; unset rungs inherit; nothing set = the declared default.
 *
 * A feature flag answers "is this rolled out to them?". "Did they pay for it?"
 * is a plan entitlement (`useEntitled`), and "may this person do it?" is a scope
 * (`usePermissions`) — gate on those, not on a flag.
 *
 * ## How the layer is fed
 *
 * One read of management-api `GET /features` per tenancy — react-query, keyed by
 * the full customer/org/tenant/user identity with an infinite `staleTime`, so it
 * runs once per session and again on a tenant or org switch. Its answer is
 * pushed through `featureFlagLayer` (declared flags only, defaults filled) onto
 * `FeatureFlags.setClaims` — the layer the JWT `feature_flags` claim used to
 * occupy, which nothing ever wrote. Precedence is unchanged:
 * `override > policy > claim (these flags) > default`, so a `?debug=1` override
 * still wins for support.
 *
 * Fails to the DEFAULTS, never to off: while the read is in flight, after it
 * fails, and for a declared flag the response omits, the layer holds each flag's
 * declared default. A transient refetch failure keeps the tenancy's last good
 * answer rather than flipping every flag back.
 *
 * Renders nothing. Mounted inside `<FlagsProvider>` (it writes that store) and
 * only when a session exists (the read needs a tenancy).
 */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useFeatureFlags } from "@authe/ui-core/flags";
import {
  FEATURE_FLAGS,
  featureFlagLayer,
  type FeatureFlagDefinition,
} from "@authe/fe-commons/features";
import type { StorageIdentity } from "@authe/fe-commons/storage";
import { useFeaturesSource } from "@/api/adapters";
import { useStorageIdentity } from "./storageIdentity";

/** One entry per tenancy: a switch reads afresh instead of reusing the last answer. */
export function featureFlagsQueryKey(identity: StorageIdentity) {
  return [
    "feature-flags",
    identity.customerId,
    identity.orgId,
    identity.tenantId,
    identity.userId,
  ] as const;
}

export interface DashboardFeatureFlagsProps {
  /** The declared flags. Defaults to the shared registry; tests pass their own. */
  registry?: readonly FeatureFlagDefinition[];
}

export function DashboardFeatureFlags({
  registry = FEATURE_FLAGS,
}: DashboardFeatureFlagsProps): null {
  const flags = useFeatureFlags();
  const source = useFeaturesSource();
  const identity = useStorageIdentity();

  const { data } = useQuery({
    queryKey: featureFlagsQueryKey(identity),
    queryFn: ({ signal }) => source.getFeatureFlags(signal),
    enabled: Boolean(identity.customerId || identity.orgId || identity.tenantId),
    staleTime: Infinity,
  });

  // `data` is undefined while loading and after a failed first read, which
  // `featureFlagLayer` answers with every declared default.
  const layer = React.useMemo(() => featureFlagLayer(data ?? null, registry), [data, registry]);

  React.useEffect(() => {
    flags.setClaims(layer);
  }, [flags, layer]);

  return null;
}
